"use client"
import { fontWeights } from "@/utils/fonts";
import Image from "next/image";
import { useEffect, useState } from "react";
import AllIconsComponent from "../custom-icons/AllIconsComponent";

export interface StatItem {
  value: number;
  label: string;
  suffix?: string;
}

export interface ImageAndSummaryProps {
  image?: string;
  title?: string;
  arrowImage?: string;
  stats?: StatItem[];
}

export default function ImageAndSummary({
  image,
  title,
  arrowImage,
  stats = []
}: ImageAndSummaryProps) {
  const [counts, setCounts] = useState<number[]>([]);

  useEffect(() => {
    // Initialize counts to 0 whenever stats change
    const initialCounts = (stats || []).map(() => 0);
    setCounts(initialCounts);

    const intervals = (stats || []).map((stat, index) => {
      const num = stat.value || 0;
      let currentCount = 0;
      const step = Math.ceil(num / 100) || 1; // Faster animation

      const interval = setInterval(() => {
        currentCount += step;
        if (currentCount >= num) {
          clearInterval(interval);
          currentCount = num;
        }
        setCounts(prevCounts => {
          const newCounts = [...prevCounts];
          if (newCounts.length > index) {
            newCounts[index] = currentCount;
          }
          return newCounts;
        });
      }, 30);

      return interval;
    });

    return () => intervals.forEach(clearInterval);
  }, [stats]);

  if (!image && !title && (!stats || stats.length === 0)) return null;

  return (
    <div>
      <div className="px-4 sm:px-8 mb-4 sm:mb-8 lg:mb-12 xl:mb-16 2xl:mb-20">
        <div
          className="
    w-full
    relative
    h-[120px]
    tsm-350:h-[180px]
    sm:h-[280px]
    md:h-[350px]
    lg:h-[450px]
    xl:w-[1160px] xl:h-[500px]
    2xl:w-[1320px] 2xl:h-[600px]
    mx-auto
  "
        >
          {image && (
            <Image
              src={image}
              alt="Summary Mask Image"
              fill
              style={{ objectFit: "contain" }}
              sizes="(max-width: 1280px) 100vw, 1160px"
              priority
            />
          )}
        </div>
      </div>
      <div className="bg-[#002F6D]/10 w-full my-auto justify-center">
        <div className="justify-items-center tsm-350:justify-between items-center tsm-350:flex mx-auto
        px-4 
        sm:px-8 
        xl:px-0
        max-w-full 
        xl:max-w-[1120px] 
        2xl:max-w-[1300px] 
        h-auto 
        tsm-350:h-[50px] 
        tsm-400:h-[60px] 
        sm:h-[70px] 
        md:h-[90px] 
        lg:h-[100px]
        xl:h-[110px]
        2xl:h-[130px]
        ">
          <div className="flex justify-center tsm-350:justify-start items-center gap-2 tsm-350:gap-0 text-[#002F6D] py-1.5 tsm-350:py-0">
            <span
              className="mx-auto h-auto text-[#002F6D] mb-0
        w-full 
        tsm-350:w-[75px] 
        tsm-400:w-[90px] 
        sm:w-[120px] 
        md:w-[140px] 
        lg:w-[160px] 
        xl:w-[170px] 
        2xl:w-[208px] 
        text-[12px]
        tsm-350:text-[12px] 
        tsm-400:text-[14px] 
        sm:text-[18px]
        md:text-[20px] 
        lg:text-[25px] 
        2xl:text-[32px] 
        "
              style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.bold, lineHeight: "1.2" }}
              dangerouslySetInnerHTML={{ __html: title || "" }}
            />
            {arrowImage ? (
              <div className="relative w-[15px] tsm-350:w-[20px] md:w-[30px] lg:w-[40px] aspect-square flex-shrink-0">
                <Image src={arrowImage} alt="Arrow" fill style={{ objectFit: 'contain' }} />
              </div>
            ) : (
              <AllIconsComponent iconName={'rightSideArrowIcon'} width={"40px"} height={"40px"} color="#002F6D" />
            )}
          </div>

          <div className="tsm-350:flex flex-row items-center 
      space-y-1
      tsm-350:space-y-0 
      tsm-350:space-x-[8px]
      tsm-400:space-x-[10px] 
      sm:space-x-[18px] 
      md:space-x-[28px] 
      lg:space-x-[38px] 
      xl:space-x-[55px] 
      2xl:space-x-[70px]
      pb-1.5
      tsm-350:pb-0
      ">
            {(stats || []).map((stat, index) => (
              <div key={index} className="text-center">
                <span className="text-[#002F6D]
            text-[13px]
            tsm-350:text-[14px]
            tsm-400:text-[16px]
            sm:text-[20px]
            md:text-[25px]
            lg:text-[30px]
            xl:text-[33px]
            2xl:text-[40px]
            tabular-nums
            inline-block
            w-[3.5ch]
            "
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold, lineHeight: "1.2" }}
                > {counts[index] ?? 0}+</span>
                <p className="text-[#000000]
            text-[10px]
            tsm-350:text-[9px] 
            tsm-400:text-[11px] 
            sm:text-[14px]
            md:text-[16px] 
            lg:text-[19px] 
            xl:text-[21px] 
            2xl:text-[24px]"
                  style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium, lineHeight: "1.2" }}
                >{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className=" lg:py-[70px] sm:py-[50px] tsm-350:py-[30px] py-[25px]">
        <div className="lg:h-[1px] h-[0.99px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto" />
      </div>
    </div>
  );
}